import React from 'react';
import { Alert, Card } from 'antd';
import { useAppSelector } from '../../hooks/redux';
import './cartSummaryDetails.scss';
import Spinner from '../spinner';
import fallbackImage from '../../assets/img/ImgFV.jpg';
import { getCartItemValidationIssue } from '../../utils/cartValidation';
import { formatSizeLabel } from '../../utils/sizeOptions';
import { getCheckoutTotal, getSalesTaxDisplayValue } from '../../config/salesTax';

const CartSummaryDetails = () => {
  const cartItems = useAppSelector((state) => state.shoppingCart.cartItems);
  const materialPriceAndImgForCart = useAppSelector((state) => state.shoppingCart.materialPriceAndImgForCart);
  const totalAmount = useAppSelector((state) => state.shoppingCart.totalAmount);
  const taxAmount = useAppSelector((state) => state.shoppingCart.taxAmount);
  const taxRate = useAppSelector((state) => state.shoppingCart.taxRate);
  const taxStatus = useAppSelector((state) => state.shoppingCart.taxStatus);
  const loadingMaterialById = useAppSelector((state) => state.shoppingCart.loadingMaterialById);
  const imgStatus = useAppSelector((state) => state.shoppingCart.imgStatus);
  const salesTaxDisplayValue = getSalesTaxDisplayValue({ taxAmount, taxStatus });
  const totalWithTax = getCheckoutTotal(totalAmount, taxAmount);

  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = fallbackImage;
  };

  return (
    <Card title="Order Details" className="cart-summary-details">
      {imgStatus === 'failed' ? (
        <Alert message="Error loading cart" type="error" />
      ) : (imgStatus === 'loading' || (imgStatus === 'idle' && cartItems.length === 0)) ? (
        <div className="cart-summary-loading">
          <Spinner />
        </div>
      ) : (
      <div>
        {cartItems.map((item) => {
          const materialData = materialPriceAndImgForCart.find(
            (entry) => entry.itemId === item.id
          );

          const isLoading = loadingMaterialById[item.id];
          const imageUrl = materialData?.materialImages?.[0]?.image?.publicUrl || fallbackImage;
          const price = materialData?.materialPrice ?? 0;
          const validationIssue = getCartItemValidationIssue(item, materialData);

          return (
            <div key={item.id} className="order-summary-detail order-summary-item">
              <div className="cart-img-details">
              {isLoading ? (
                  <div className="spinner-container">
                    <Spinner />
                  </div>
                ) : (
                  <img
                    src={imageUrl}
                    alt={item.product.title}
                    onError={handleImageError}
                  />
                )}
              </div>
              <div className="cart-details-sum">
                <h5 className="title">{item.product.title}</h5>
                <p>Material: {item.selectedMaterial}</p>
                {item.selectedSize && <p>Size: {formatSizeLabel(item.selectedSize)}</p>}
                {validationIssue && (
                  <p className="cart-summary-warning">{validationIssue.message}</p>
                )}
              </div>
              <span className="order-detail-divider" aria-hidden="true" />
              <p className="order-item-price">${(Number(price) * Number(item.quantity)).toFixed(2)}</p>
            </div>
          );
        })}

        <div className="order-summary-detail">
          <p>
            Sales Tax Estimate
            {taxRate > 0 && ` (${(taxRate * 100).toFixed(2)}%)`}:
          </p>
          <p>
            {salesTaxDisplayValue}
          </p>
        </div>
        <div className="order-summary-detail">
          <p>Total:</p>
          <p>${totalWithTax.toFixed(2)}</p>
        </div>
      </div>
      )}
    </Card>
  );
};

export default CartSummaryDetails;
